import React, { FC } from "react";
import { ExpertRole } from "../../data/constants";
import { AssignedUserViewModel } from "../../model/AssignedUserViewModel";
import { ExpertViewModel } from "../../model/ExpertViewModel";
import { RoomAssignmentViewModel } from "../../model/RoomAssignmentViewModel";
import UserSelect, { UserSelectProps } from "./UserSelect";
import { ExamDayViewModel } from "../../model/ExamDayViewModel";
import { ActionMeta, ValueType } from "react-select";
import { OptionType } from "../common/customSelect/CustomSelect";
import { ExamDayType } from "../../model/ExamDayType";
import { ExamSessionViewModel } from "../../model/ExamSessionViewModel";
import { rolesAssignedToCourseTypes } from "../expert/ExpertConstants";

export interface RoomAssignmentExpertSelectProps {
  examSession: ExamSessionViewModel;
  allExperts?: ExpertViewModel[];
  examDay: ExamDayViewModel;
  expertRole: ExpertRole;
  isFixedExpert: boolean;
  label?: JSX.Element;
  placeholder?: JSX.Element;
  minimumUsers?: number;
  showAllExperts: boolean;
  roomAssignment: RoomAssignmentViewModel;
  change: (isFixed: boolean, experts: AssignedUserViewModel[]) => void;
}

const RoomAssignmentExpertSelect: FC<RoomAssignmentExpertSelectProps> = ({
  examSession,
  allExperts,
  expertRole,
  isFixedExpert,
  minimumUsers,
  label,
  placeholder,
  showAllExperts,
  examDay,
  roomAssignment,
  change,
}) => {
  if (allExperts === undefined) {
    return <></>;
  }

  const getAssignedUsers = (isFixed: boolean) =>
    roomAssignment.assignedUsers.filter((user) => user.isFixed === isFixed);

  const filterAvailableUsers = (
    availableUsers: ExpertViewModel[],
    assignedUsers: AssignedUserViewModel[],
    userRole: ExpertRole,
    roomAssignmentLanguages: string[],
    assignedExamIds: string[]
  ) => {
    return availableUsers.filter(
      (user) =>
        user.rolesWithExamsAndCourseTypes.filter(
          (roleExam) =>
            roleExam.role === userRole && roleExam.examIds.some((examId) => assignedExamIds.indexOf(examId) >= 0)
        ) && // expert has required role for at least one of the required exams
        !assignedUsers.map((assignedUser) => assignedUser.userId).includes(user.id) && // expert is not already assigned
        user.languages.some((language) => roomAssignmentLanguages.includes(language)) // expert has at least one required language
    );
  };

  const getRequiredExamIds = () => {
    if (roomAssignment.isPreparationRoom) {
      var examIds = new Set<string>();
      examDay.roomAssignments.forEach((dayRoomAssignment) => {
        dayRoomAssignment.examAssignments.forEach((examAssignment) => {
          examIds.add(examAssignment.examId);
        });
      });
      return Array.from(examIds);
    }

    return roomAssignment.examAssignments.map((examAssignment) => examAssignment.examId);
  };

  const getRequiredCourseTypes = (examSession: ExamSessionViewModel, examIds: string[]) => {
    return examSession.exams
      .filter((exam) => examIds.includes(exam.id))
      .map((exam) => exam.courseTypes)
      .flat();
  };

  const getExpertSelectsProps = (examSession: ExamSessionViewModel) => {
    const allAssignedUsers = getAllAssignedExpertsForDay();
    const examIds = getRequiredExamIds();
    const requiredCourseTypes = getRequiredCourseTypes(examSession, examIds);

    const getAvailableUsers = () => {
      if (showAllExperts) {
        return allExperts.filter((e) =>
          e.rolesWithExamsAndCourseTypes.some(
            (re) =>
              re.role === expertRole &&
              (rolesAssignedToCourseTypes.includes(expertRole)
                ? re.courseTypes.some((courseType) => requiredCourseTypes.includes(courseType))
                : re.examIds.some((examId) => roomAssignment.examAssignments.map((ea) => ea.examId).includes(examId)))
          )
        );
      }
      const experts =
        examDay.type === ExamDayType.Correction ? examDay.availableCorrectionExperts : examDay.availableUsers;
      return experts.filter((e) =>
        e.rolesWithExamsAndCourseTypes.some(
          (re) =>
            re.role === expertRole &&
            (rolesAssignedToCourseTypes.includes(expertRole)
              ? re.courseTypes.some((courseType) => requiredCourseTypes.includes(courseType))
              : re.examIds.some((examId) => roomAssignment.examAssignments.map((ea) => ea.examId).includes(examId)))
        )
      );
    };
    const availableExperts = filterAvailableUsers(
      getAvailableUsers(),
      allAssignedUsers,
      expertRole,
      roomAssignment.languages,
      examIds
    );
    return {
      expertProps: getExpertsProps(availableExperts),
    };
  };

  const getAllAssignedExpertsForDay = () => {
    const dateString = new Date(examDay.date).toDateString();
    return examSession.examDays
      .filter((d) => new Date(d.date).toDateString() === dateString)
      .map((d) => d.roomAssignments.map((r) => r.assignedUsers).flat())
      .flat();
  };

  const handleChange =
    (areFixed: boolean) => (selectedExperts: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => {
      const experts =
        (actionMeta && actionMeta.action === "clear") || !selectedExperts
          ? []
          : (selectedExperts as OptionType[]).map((selectedUser) => {
              return {
                isFixed: areFixed,
                userId: selectedUser.value,
              };
            });
      change(areFixed, experts);
    };

  const getExpertsProps = (availableExperts: ExpertViewModel[]): UserSelectProps => {
    const assignedUsers = getAssignedUsers(isFixedExpert);
    const selectedExperts = allExperts.filter(
      (expert) => assignedUsers.filter((assignedUser) => assignedUser.userId === expert.id).length > 0
    );
    return {
      availableUsers: availableExperts,
      selectedUsers: selectedExperts,
      label: label,
      placeholder: placeholder,
      onUserSelectChange: handleChange(isFixedExpert),
      minimumSelectedUsers: minimumUsers,
    };
  };

  const { expertProps } = getExpertSelectsProps(examSession);

  const nameIdentifyier = isFixedExpert ? "fixed-user-select-" : "reserve-user-select-";
  return <UserSelect {...expertProps} name={`${nameIdentifyier}${roomAssignment.id}`} />;
};

export default RoomAssignmentExpertSelect;
